import { Component, OnInit, Optional } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { FilesService } from '../../../services/files.service';
import { FileInfoFactory } from '../../models/file-info/file-info.factory';
import { IFile, IFileInfo, RawFileInfo } from '../../models/file-info';
import { PAGE_SIZE } from '../../main.utilities';

/** File names the Region Editor imports regions from (GeoJSON, label volumes, their ontology). */
export const REGION_FILE_PATTERN = /\.(geojson|json|nii|nii\.gz|nrrd)$/i;
/** Label volumes, imported one region source per slice. */
const LABEL_VOLUME_PATTERN = /\.(nii|nii\.gz|nrrd)$/i;

/** A path's last segment. */
const leafName = (path: string) => path.split(/[\\/]/).pop() ?? path;

/**
 * The Region Editor's "Import regions from server" picker: a file tree of the current project's
 * bucket (every project when no image is open), where a folder of per-slice region files, a
 * GeoJSON file, or a label volume (`.nii`, `.nii.gz`, `.nrrd`) chosen together with its ontology
 * JSON is picked. Closes with the picked entries' raw infos, or undefined when cancelled.
 *
 * Its own tree, as the QuPath script picker's (`StorageScriptPickerComponent`): the sidebar's is
 * the one global selection the rest of the app reads. Folders page as the sidebar does.
 */
@Component({
  selector: 'region-picker',
  templateUrl: './region-picker.component.html',
  styleUrls: ['./region-picker.component.scss'],
})
export class RegionPickerComponent implements OnInit {
  nodes: TreeNode<IFileInfo>[] = [];
  loading = false;
  selected: TreeNode<IFileInfo>[] = [];

  private loadedNodes = new Set<TreeNode<IFileInfo>>();

  constructor(
    private filesService: FilesService,
    private ref: DynamicDialogRef,
    @Optional() private config?: DynamicDialogConfig,
  ) {}

  /** The bucket to browse (the open image's project), or every project without one. */
  private get bucket(): string | undefined {
    return this.config?.data?.bucket as string | undefined;
  }

  ngOnInit(): void {
    this.loading = true;
    this.filesService.getProjects().subscribe({
      next: (raw: RawFileInfo[]) => {
        const bucket = this.bucket;
        const projects = raw
          .filter((r) => !bucket || r.project?.bucket === bucket)
          .map((r) => FileInfoFactory.getFileInfo(r));
        this.nodes = this.filesService.createNodes(projects);
        this.loading = false;
        // One project: open it straight away.
        if (this.nodes.length === 1) {
          this.nodes[0].expanded = true;
          this.onNodeExpand({ node: this.nodes[0] });
        }
      },
      error: () => (this.loading = false),
    });
  }

  onNodeExpand(event: { node: TreeNode<IFileInfo> }): void {
    const node = event.node;
    if (!node.data || this.loadedNodes.has(node)) return;
    this.loadedNodes.add(node);
    this.loadPage(node, 0);
  }

  onLoadMore(event: Event, sentinel: TreeNode): void {
    event.stopPropagation();
    const folder = sentinel.parent as TreeNode<IFileInfo> | undefined;
    if (!folder?.data) return;
    sentinel.type = 'loadingmore';
    this.loadPage(folder, sentinel.data.nextPage);
  }

  private loadPage(node: TreeNode<IFileInfo>, page: number): void {
    node.loading = true;
    this.filesService.getChildren(node.data!.rawData, page, PAGE_SIZE).subscribe({
      next: (raw: RawFileInfo[]) => {
        node.children = (node.children ?? []).filter((c) => c.type !== 'loadmore' && c.type !== 'loadingmore');
        const newNodes = this.filesService.createNodes(raw.map((f) => FileInfoFactory.getFileInfo(f)));
        newNodes.forEach((n: TreeNode<IFileInfo>) => {
          n.parent = node;
          // Only what regions import from can be picked; other files are shown, greyed.
          if (!this.isPickable(n)) n.selectable = false;
        });
        node.children.push(...newNodes);
        if (raw.length === PAGE_SIZE) {
          const sentinel = this.filesService.createLoadMoreNode(node.data!.rawData, page + 1);
          sentinel.parent = node;
          node.children.push(sentinel as TreeNode<IFileInfo>);
        }
        node.loading = false;
      },
      error: () => {
        node.children?.forEach((c) => {
          if (c.type === 'loadingmore') c.type = 'loadmore';
        });
        if (page === 0) this.loadedNodes.delete(node);
        node.loading = false;
      },
    });
  }

  /** A folder (of per-slice region files), or a region, label-volume or ontology file. */
  isPickable(node: TreeNode<IFileInfo> | null | undefined): boolean {
    const data = node?.data;
    if (!data?.rawData) return false;
    if (data.rawData.type === 'Folder') return true;
    return data instanceof IFile && REGION_FILE_PATTERN.test(data.name ?? data.relPath ?? '');
  }

  /** What may be imported: one folder, or files the Region Editor imports whole (see {@link importProblem}). */
  get canImport(): boolean {
    return this.selected.some((n) => this.isPickable(n)) && !this.importProblem;
  }

  /**
   * Why the picked entries cannot be imported as they are, or null. The Region Editor takes one
   * folder; or one label volume with at most one ontology JSON; or GeoJSON files (one, or a slice
   * each) with at most a `regionset.json`; or JSON region files. It would silently use part of any
   * other combination (the first volume of two, a volume without the GeoJSON beside it), so those
   * are refused here, saying why.
   */
  get importProblem(): string | null {
    const picked = this.selected.filter((n) => this.isPickable(n));
    if (!picked.length) return null;
    const folders = picked.filter((n) => n.data!.rawData.type === 'Folder');
    if (folders.length) return picked.length === 1 ? null : 'A folder imports on its own: pick only the folder.';
    const names = picked.map((n) => n.data!.name ?? n.data!.relPath ?? '');
    const volumes = names.filter((n) => LABEL_VOLUME_PATTERN.test(n));
    const geojsons = names.filter((n) => /\.geojson$/i.test(n));
    const jsons = names.filter((n) => /\.json$/i.test(n) && !/\.geojson$/i.test(n));
    if (volumes.length > 1) return 'Pick one label volume at a time.';
    if (volumes.length === 1) {
      if (geojsons.length) return 'A label volume imports with its ontology JSON only: pick the GeoJSON files on their own.';
      if (jsons.length > 1) return 'Pick one ontology JSON with the label volume.';
      return null;
    }
    const others = jsons.filter((n) => leafName(n).toLowerCase() !== 'regionset.json');
    if (geojsons.length && others.length) {
      return 'With GeoJSON files only a regionset.json is used: leave the other JSON files out.';
    }
    return null;
  }

  onImport(): void {
    if (!this.canImport) return;
    this.ref.close(this.selected.filter((n) => this.isPickable(n)).map((n) => n.data!.rawData));
  }

  onCancel(): void {
    this.ref.close(undefined);
  }
}
